import { Breadcrumbs, Stack, Typography } from '@mui/material'
import { LinkRouter } from 'components/breadcrumbs'
import { useParams } from 'react-router-dom'
import { PopupMenu } from 'components/common/SubMenuPopup'
import { getKeyValueOrDefaultNested } from 'utils/utils'
import FacilityConfigContext from 'contexts/FacilityConfigContext'
import { useContext } from 'react'
import { useModulePermission } from 'hooks/useModulePermission'
import { usePermission } from 'hooks/usePermission'

type IBarrierConfigurationSubMenu = {
  audit_id?: string | undefined
  asset_name?: string
}

export default function BarrierConfigurationSubMenu({
  audit_id,
  asset_name,
}: IBarrierConfigurationSubMenu) {
  const { project_id, asset_id } = useParams()

  const { config } = useContext(FacilityConfigContext)
  const { checkProjectPermission } = useModulePermission()
  const { checkSubprojectPermission } = usePermission()


  // project level permissions
  const showBarrierManagement = checkProjectPermission({ action: "BarrierManagement:GetModuleAccess", type: "read" })
  const showProcessSafetyElements = checkProjectPermission({ action: "ProcessIntegrity:GetModuleAccess", type: "read" })
  const showAssuranceScorecard = checkProjectPermission({ action: "AssuranceScorecard:GetModuleAccess", type: "read" })
  const showActionManagement = checkProjectPermission({ action: "ActionManagement:GetModuleAccess", type: "read" })


  const PSELabel = getKeyValueOrDefaultNested(config, "PSE_LABEL.ABBREVIATION_PRIMARY", "PSE")

  const hasSubPermsn = (action: string | string[], type: any) => {
    return checkSubprojectPermission({ action, type, assetId: asset_id! })
  }

  const setupMenuItems = [
    {
      label: 'Barrier Configuration',
      url: `/hazard-barrier-configuration/project/${project_id}/asset/${asset_id}/configuration`,
      disabled: !hasSubPermsn(["GetModuleAccess", "ListQuestion"], ["read", "list"]),
    },
    {
      label: 'Score',
      url: `/hazard-barrier-configuration/project/${project_id}/asset/${asset_id}/scores`,
      disabled: !hasSubPermsn(["GetModuleAccess"], "read"),
    },
  ]

  const loadsheetMenuItems = [
    {
      label: 'Findings',
      url: `/hazard-barrier-configuration/project/${project_id}/asset/${asset_id}/loadsheet-actions-findings`,
      disabled: !hasSubPermsn("BatchDownloadFinding", "list") && !hasSubPermsn("BatchUpdateFinding", "write"),
    },
    {
      label: 'Setup',
      url: `/hazard-barrier-configuration/project/${project_id}/asset/${asset_id}/loadsheet-setup`,
      disabled: !hasSubPermsn("BatchDownloadSetup", "list") && !hasSubPermsn("BatchUpdateSetup", "write"),
    },
    {
      label: 'Actions',
      url: `/action-management/project/${project_id}/asset/${asset_id}/barrier/action-loadsheet`,
      disabled: !hasSubPermsn("BatchDownloadAssessmentFormAction", "list") && !hasSubPermsn("BatchCreateUpdateAssessmentFormAction", "write"),
    },
  ]

  const analysisMenuItems = [
    {
      label: 'Vulnerability Analysis',
      url: `/hazard-barrier-evaluation/project/${project_id}/asset/${asset_id}/vulnerability-analysis`,
    },
    {
      label: 'Pipelines Survivability Analysis',
      url: `/hazard-barrier-evaluation/project/${project_id}/asset/${asset_id}/pipelines-survivability`,
    },
  ]

  const dashboardMenuItems = [
   {
      label: 'Barrier Dashboard',
      url: `/hazard-barrier-evaluation/project/${project_id}/asset/${asset_id}`,
      disabled: !showBarrierManagement,
    },
    {
      label: `${PSELabel} Dashboard`,
      url: `/process-safety-elements/project/${project_id}/asset/${asset_id}/dashboard`,
      disabled: !showProcessSafetyElements,
    },
    {
      label: 'Assurance Scorecard Dashboard',
      url: `/assurance-scorecard/project/${project_id}/asset/${asset_id}`,
      disabled: !showAssuranceScorecard,
    },
  ]

  const ActionManagementMenuItems = [
    {
      label: 'Action Management',
      url: `/action-management/project/${project_id}/`,
      disabled: !showActionManagement,
    },
    {
      label: 'Loadsheet',
      url: `/action-management/project/${project_id}/action-management/action-loadsheet`,
      disabled: !showActionManagement,
    },
  ]

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={2}
      display={'flex'}
      alignContent={'center'}
      sx={{
        paddingBottom: 2,
        backgroundColor: '#0070B4',
        paddingLeft: 3,
        borderColor: 'white',
        minWidth: 500,
      }}
    >
      {(() => {
        const isAllDisabled = (items: { disabled?: boolean }[]) => items.every(i => i.disabled)

        const breadcrumbItems: JSX.Element[] = []

        // Add menus only if they would render (avoid null children creating gaps)
        if (!isAllDisabled(dashboardMenuItems)) {
          breadcrumbItems.push(
            <PopupMenu label="Dashboard" items={dashboardMenuItems} key="dashboard-menu" showOnAllPermission />
          )
        }

        if (!isAllDisabled(setupMenuItems)) {
          breadcrumbItems.push(
            <PopupMenu label="Setup" items={setupMenuItems} key="setup-menu" showOnAllPermission showInList />
          )
        }

        if (!isAllDisabled(loadsheetMenuItems)) {
          breadcrumbItems.push(
            <PopupMenu label="Loadsheet" items={loadsheetMenuItems} key="loadsheet-menu" showOnAllPermission showInList />
          )
        }

        if (!isAllDisabled(ActionManagementMenuItems)) {
          breadcrumbItems.push(
            <PopupMenu label="Action Management" items={ActionManagementMenuItems} key="action-management-menu" showOnAllPermission showInList />
          )
        }

        // Static link - always present
        breadcrumbItems.push(
          <LinkRouter underline="hover" color="inherit" to={`#`} variant="body2DMSans" key="reports-link">
            <Typography variant="body2DMSans" fontSize={14} color="white">
              Reports
            </Typography>
          </LinkRouter>
        )

        // Conditional analysis menu
        if (asset_name === 'Mellitah Gas Complex (MCX)') {
          breadcrumbItems.push(
            <PopupMenu label="Analysis" items={analysisMenuItems} key="analysis-menu" />
          )
        }

        return (
          <Breadcrumbs separator={<></>} sx={{ paddingLeft: 3, paddingTop: 2 }} maxItems={999}>
            {breadcrumbItems}
          </Breadcrumbs>
        )
      })()}
    </Stack>
  )
}
